import Layout from "@/components/elements/Layout";
import ShareButtons from "@/components/parts/ShareButtons";
// import InfobytesSlider from "@/components/parts/InfobytesSlider";
import { userAtom } from "@/Atoms";
import RelatedContentSidebar from "@/components/RelatedContentSidebar";
import SubNavigation from "@/components/elements/SubNavigation";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import ShareButtonsVertical from "@/components/parts/ShareButtonsVertical";
import Schema from "@/components/schema/Schema";
import { useGetCurrentUrl, useGetRelatedContents } from "@/hooks";
import {
  getContentDetailBySlug,
  getRelatedContentName,
  trackContentAccessCounting,
} from "@/services/api";
import { CONTENT_TYPES, cleanHTML } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import { fitIn } from "@/utils/imageHandler";
import {
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
  TwitterLogo,
} from "@phosphor-icons/react";
import { useAtom } from "jotai";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import { Zoom } from "yet-another-react-lightbox/plugins";
import "yet-another-react-lightbox/styles.css";

function InfobyteDetail({ infobyte = {} }) {
  const [reach_limit, setReachLimit] = useState(false);
  const [related_items, isLoading] = useGetRelatedContents(
    CONTENT_TYPES.INFOBYTES,
    infobyte.uuid
  );
  const [expandedAuthors, setExpandedAuthors] = useState({});

  const [isOpen, setIsOpen] = useState(0);
  const router = useRouter();
  const fullUrl = useGetCurrentUrl();
  const [userState, setUserState] = useAtom(userAtom);

  const zoomRef = useRef(null);

  const structuredData = [
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Infobytes",
          item: `${process.env.BASE_URL}infobytes`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: infobyte.title,
          item: `${process.env.BASE_URL}infobytes/${router.query.slug}`,
        },
      ],
    },
  ];

  function goBack() {
    // window.history.back();
    router.push(
      {
        pathname: "/infobytes",
        query: { ...router.query },
      },
      undefined,
      { scroll: false }
    );
  }

  const fetchMeteredContent = async () => {
    if (userState?.roles === "admin-team" || userState?.roles === "read-access") {
      setReachLimit(false);
    } else {
      try {
        const response = await trackContentAccessCounting(router.query.slug, {
          ip_address: localStorage.getItem("lctd-ia"),
          user_id: userState?.id,
          user_email: userState?.email,
        });
        setReachLimit(response.data.reach_limit);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchMeteredContent();
  }, [router.query.slug, userState]);

  const toggleAuthorDescription = (authorId) => {
    setExpandedAuthors((prev) => ({
      ...prev,
      [authorId]: !prev[authorId],
    }));
  };

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        title={infobyte.title}
        meta_title={infobyte.meta_title}
        description={infobyte.meta_desc}
        meta_image={infobyte.thumbnail_url}
      >
        <SubNavigation title={infobyte.title}></SubNavigation>
        <div className='max-w-6xl px-4 mx-auto lg:flex'>
          <div className='flex items-start flex-1 pt-6 pb-10 lg:pt-10 lg:pb-16 lg:pr-8 lg:mr-8 lg:border-r lg:space-x-8 2xl:space-x-12 border-white/5'>
            <div className='sticky hidden pt-2 lg:block top-40'>
              <ShareButtonsVertical
                title={infobyte.title}
                description={infobyte.meta_desc}
                url={fullUrl}
              />
            </div>
            <div>
              <h1 className='text-2xl font-medium leading-[1.1] lg:font-semibold lg:text-4xl font-display'>
                {infobyte.title}
              </h1>
              <div className='flex items-center justify-between gap-4 mt-3 mb-5'>
                <p className='flex items-center text-sm'>
                  <span className='opacity-40'>
                    {`${formatDate(infobyte.publish_date, "DD MMMM YYYY")}`}
                  </span>
                  <span className='ml-2 opacity-40'>&bull;</span>
                  <ContentWishlistComponent item={infobyte} />
                </p>
                <span className='flex-1 h-px bg-white/10'></span>
                <div className='lg:hidden'>
                  <ShareButtons
                    title={infobyte.title}
                    description={infobyte.meta_desc}
                    url={fullUrl}
                  />
                </div>
              </div>
              <div
                className='relative w-full hover:cursor-pointer'
                onClick={() => setIsOpen(true)}
              >
                <Image
                  src={fitIn(infobyte.image_url, 1500)}
                  alt={infobyte.image_alt}
                  width={720}
                  height={540}
                  layout={"responsive"}
                  objectFit={"contain"}
                  className='absolute inset-0 w-full h-full'
                />
                <Lightbox
                  open={isOpen}
                  close={() => setIsOpen(false)}
                  slides={[{ src: infobyte.image_url }]}
                  plugins={[Zoom]}
                  zoom={{ ref: zoomRef }}
                  render={{
                    buttonPrev: () => null,
                    buttonNext: () => null,
                  }}
                />
              </div>

              <div className='py-4 text-sm text-white/70'>
                By{" "}
                {infobyte.authors.map((author, index) => (
                  <Link
                    key={index}
                    href={`/author/${author.slug}`}
                    className='no-underline'
                  >
                    <span className='underline'>{author.name}</span>
                    {infobyte.authors.length > 2 &&
                      index < infobyte.authors.length - 2 &&
                      ", "}
                    {infobyte.authors.length > 1 &&
                      index === infobyte.authors.length - 2 &&
                      " and "}
                  </Link>
                ))}{" "}
              </div>

              <div className='prose dark:prose-invert max-w-none'>
                {cleanHTML(infobyte.description)}
              </div>

              {/* Authors Section */}
              {infobyte.authors && infobyte.authors.length > 0 && (
                <div className='pt-8 mt-12 border-t border-white/10 not-prose'>
                  <h3 className='mb-4 text-lg font-semibold'>
                    {infobyte.authors.length === 1 ? "Author" : "Authors"}
                  </h3>
                  <div className='space-y-2 lg:space-y-4'>
                    {infobyte.authors.map((author, index) => (
                      <div
                        key={author.uuid}
                        className='flex items-start p-4 space-x-4 border rounded-lg bg-white/5 border-white/5'
                      >
                        <div className='relative flex-shrink-0 size-14 lg:size-20'>
                          {author.photo_profile_url ? (
                            <Image
                              src={author.photo_profile_url}
                              alt={
                                author.photo_profile_alt ||
                                `${author.name} profile photo`
                              }
                              layout='fill'
                              className='object-cover rounded-full'
                            />
                          ) : (
                            <div className='flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-1 to-red-1'>
                              <span className='text-xl font-bold text-white'>
                                {author.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className='flex-1'>
                          <div className='flex items-start space-x-3'>
                            <div className='flex-1'>
                              <Link
                                href={`/author/${author.slug}`}
                                className='no-underline'
                              >
                                <h4
                                  className={`text-lg  leading-tight font-semibold text-white hover:text-yellow-1 transition-colors ${
                                    author.description ? "mt-0" : ""
                                  }`}
                                >
                                  {author.name}
                                </h4>
                              </Link>
                            </div>
                            <div className='flex items-center space-x-3'>
                              {author.linkedin_url && (
                                <a
                                  href={author.linkedin_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='transition-colors text-white/60 hover:text-white'
                                >
                                  <LinkedinLogo size={20} weight='bold' />
                                </a>
                              )}
                              {author.twitter_url && (
                                <a
                                  href={author.twitter_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='transition-colors text-white/60 hover:text-white'
                                >
                                  <TwitterLogo size={20} weight='bold' />
                                </a>
                              )}
                              {author.facebook_url && (
                                <a
                                  href={author.facebook_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='transition-colors text-white/60 hover:text-white'
                                >
                                  <FacebookLogo size={20} weight='bold' />
                                </a>
                              )}
                              {author.instagram_url && (
                                <a
                                  href={author.instagram_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='transition-colors text-white/60 hover:text-white'
                                >
                                  <InstagramLogo size={20} weight='bold' />
                                </a>
                              )}
                            </div>
                          </div>
                          {(author.job_title || author.company_name) && (
                            <p className='mt-0 mb-3 text-xs text-white/50'>
                              {author.job_title && author.company_name
                                ? `${author.job_title} of ${author.company_name}`
                                : author.job_title || author.company_name}
                            </p>
                          )}
                          {author.description && (
                            <div className='mt-2 text-xs leading-relaxed lg:text-sm text-white/80'>
                              <p
                                className={
                                  expandedAuthors[author.id]
                                    ? ""
                                    : "line-clamp-3"
                                }
                              >
                                {author.description}
                              </p>
                              {author.description.length > 200 && (
                                <button
                                  onClick={() =>
                                    toggleAuthorDescription(author.id)
                                  }
                                  className='mt-2 text-xs font-medium transition-colors text-yellow-1 hover:text-yellow-2'
                                >
                                  {expandedAuthors[author.id]
                                    ? "Show less"
                                    : "Show more"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* <div className='mt-6 lg:mt-16'>
                <CarouselInfobyte
                  title='Related Infobytes'
                  data={related_items}
                  showMoreLink={`/infobytes`}
                  showMoreText='See All Infobytes'
                />
              </div> */}
            </div>
          </div>

          <div className='lg:pt-10 lg:max-w-[300px] text-white pb-10 lg:pb-16 space-y-8 lg:space-y-10'>
            {infobyte.connected_with && (
              <div>
                <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
                  Related Report
                </h2>
                <Link
                  href={`/reports/${infobyte.connected_with.slug}`}
                  className='relative flex items-start space-x-6 group'
                >
                  <div className='flex-1'>
                    <p className='pt-1 mb-2 text-lg font-semibold leading-tight tracking-tight text-white group-hover:underline lg:leading-tight'>
                      {infobyte.connected_with.title}
                    </p>
                    <p className='mb-3 text-xs font-medium text-white/40 line-clamp-3'>
                      {infobyte.connected_with.excerpt}
                    </p>
                    {/* <Button
                      onClick={() =>
                        router.push(`/reports/${infobyte.connected_with.slug}`)
                      }
                      className='w-full text-white bg-background hover:bg-zinc-900'
                    >
                      <BookOpen weight='bold' className='mr-2.5' />
                      <span>Read Full Report</span>
                    </Button> */}
                  </div>
                  <div className='w-20'>
                    <div className='relative aspect-[3/4] rounded overflow-hidden border border-white/10'>
                      <Image
                        src={fitIn(
                          infobyte.connected_with.cover_image_url,
                          600
                        )}
                        layout='fill'
                        objectFit='cover'
                        alt={infobyte.connected_with.cover_image_alt}
                      />
                    </div>
                  </div>
                </Link>
              </div>
            )}
            <RelatedContentSidebar
              title='Related Videos & Podcasts'
              currentContentId={infobyte.uuid}
              contentType={CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES}
            />
            <RelatedContentSidebar
              title='Did you check these out?'
              currentContentId={infobyte.uuid}
            />
          </div>
        </div>
      </Layout>
    </div>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.INFOBYTES,
      },
    });
    const infobyte = response.data.data;
    const responseRelated = await getRelatedContentName(
      CONTENT_TYPES.INFOBYTES,
      infobyte.uuid
    );

    return {
      props: {
        infobyte,
        related_items: responseRelated.data?.data || [],
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default InfobyteDetail;
